import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { useZohoSession } from '../contexts/ZohoSessionContext';
import { AddCallToCrmModal } from '../components/AddCallToCrmModal';
import { EightXEightCallsModal } from '../components/EightXEightCallsBar';
import { TaskDetailsModal, OrdersQuotesModal } from '../components/NotificationBars';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Same refresh rhythm and the same change events the Home page notification cards use (components/NotificationBars.jsx),
// so something done anywhere else in the app (a call logged, a task closed, stock requested) updates the right bar too.
const CALLS_SAFETY_NET_MS = 45 * 60 * 1000;
const PI_SAFETY_NET_MS = 5 * 60 * 1000;
const TASKS_SAFETY_NET_MS = 10 * 60 * 1000;
const DASHBOARD_SAFETY_NET_MS = 90 * 1000;
const EIGHT_X_EIGHT_POLL_MS = 20 * 1000;

const CALLS_CHANGED_EVENT = 'c30:calls-changed';
const PI_COUNTS_CHANGED_EVENT = 'c30:pi-counts-changed';
const TASKS_CHANGED_EVENT = 'c30:tasks-changed';
const STOCK_CHANGED_EVENT = 'c30:stock-changed';
const QUOTES_CHANGED_EVENT = 'c30:quotes-changed';

const DEFAULT_NOTIF_SETTINGS = {
  enabled: true,
  branch_enabled: true,
  notifications: [
    { type: 'calls', enabled: true, order: 0 },
    { type: 'tasks', enabled: true, order: 1 },
    { type: 'pi_counts', enabled: true, order: 2 },
    { type: 'stock', enabled: true, order: 3 },
    { type: 'orders_quotes', enabled: true, order: 4 },
  ],
};

const isSameLocalDay = (iso, ref) => {
  if (!iso) return false;
  const d = new Date(iso);
  return d.getFullYear() === ref.getFullYear() && d.getMonth() === ref.getMonth() && d.getDate() === ref.getDate();
};

const V3AlertsContext = createContext(null);

export const useV3Alerts = () => {
  const value = useContext(V3AlertsContext);
  if (!value) throw new Error('useV3Alerts must be used inside the V3 shell');
  return value;
};

/**
 * Everything the right bar's Tasks tab shows, fetched once for the whole V3 shell: the person's tasks, scheduled CRM calls, PI counts that
 * need action, pending stock requests, today's quotes and orders, and 8x8 calls to log. It reads exactly the same endpoints as the Home
 * page notification cards and respects the same Settings > Notifications switches. The modals those cards open (task details, log a
 * call, 8x8 calls, quotes and orders) are the existing components; they are drawn here so they survive the bar closing.
 */
export const V3AlertsProvider = ({ user, children }) => {
  const { isLinked, isAdminGroup, loading: sessionLoading } = useZohoSession();

  const [calls, setCalls] = useState([]);
  const [callsLoading, setCallsLoading] = useState(true);
  const [callsRefreshing, setCallsRefreshing] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [piCounts, setPiCounts] = useState([]);
  const [piLoading, setPiLoading] = useState(true);
  const [stockSummary, setStockSummary] = useState(null);
  const [quotesData, setQuotesData] = useState(null);
  const [notifSettings, setNotifSettings] = useState(DEFAULT_NOTIF_SETTINGS);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [stockLoading, setStockLoading] = useState(true);
  const [calls8x8Made, setCalls8x8Made] = useState([]);
  const [calls8x8Logged, setCalls8x8Logged] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [completedCalls, setCompletedCalls] = useState([]);
  const [quickNotes, setQuickNotes] = useState([]);
  const [eightTab, setEightTab] = useState('made');

  const [activeCall, setActiveCall] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null); // { task, isCompleted }
  const [show8x8, setShow8x8] = useState(false);
  const [showOrders, setShowOrders] = useState(false);

  const fetchCalls = useCallback(async () => {
    if (!isLinked) { setCalls([]); setCallsLoading(false); return; }
    try {
      const res = await api.get(`${API}/zoho/my-scheduled-calls`);
      setCalls(res.data?.calls || []);
    } catch (_) { /* keep what is shown */ }
    finally { setCallsLoading(false); }
  }, [isLinked]);

  const manualRefreshCalls = useCallback(async () => {
    setCallsRefreshing(true);
    await fetchCalls();
    setCallsRefreshing(false);
  }, [fetchCalls]);

  const fetchTasks = useCallback(async () => {
    if (!isLinked) { setTasks([]); setTasksLoading(false); return; }
    try {
      const res = await api.get(`${API}/zoho/my-tasks`);
      setTasks(res.data?.tasks || []);
    } catch (_) { /* keep what is shown */ }
    finally { setTasksLoading(false); }
  }, [isLinked]);

  const fetchPi = useCallback(async () => {
    try {
      const res = await api.get(`${API}/pi-counts/action-required-summary`);
      setPiCounts(res.data?.counts || []);
    } catch (_) { /* keep what is shown */ }
    finally { setPiLoading(false); }
  }, []);

  const fetchDashboard = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) { setStockLoading(false); setSettingsLoading(false); return; }
    try {
      const res = await api.get(`${API}/dashboard/init`, { headers: { Authorization: `Bearer ${token}` } });
      const { quotes, stock, settings } = res.data || {};
      if (quotes) setQuotesData(quotes);
      if (stock) setStockSummary(stock);
      if (settings && (Array.isArray(settings.notifications) || typeof settings.enabled !== 'undefined')) setNotifSettings(settings);
    } catch (_) { /* keep what is shown */ }
    finally { setStockLoading(false); setSettingsLoading(false); }
  }, []);

  // 8x8 quick call notes: the same notes the 8x8 Calls window's Notes tab shows. A note saved from the right bar is matched to its call by the
  // server once that call appears, exactly as a note typed in that window is.
  const fetchQuickNotes = useCallback(() => {
    api.get(`${API}/8x8/quick-notes`)
      .then((res) => setQuickNotes(res.data?.notes || []))
      .catch(() => {});
  }, []);

  const saveQuickNote = useCallback(async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed) return false;
    try {
      const res = await api.post(`${API}/8x8/quick-notes`, { text: trimmed });
      const note = res.data?.note;
      if (note && (note.text || '') !== trimmed) await api.patch(`${API}/8x8/quick-notes/${note.id}`, { text: trimmed });
      fetchQuickNotes();
      return true;
    } catch (_) { return false; }
  }, [fetchQuickNotes]);

  const fetch8x8 = useCallback(() => {
    fetchQuickNotes();
    api.get(`${API}/8x8/active-calls`)
      .then((res) => { setCalls8x8Made(res.data?.calls_made || []); setCalls8x8Logged(res.data?.calls_logged || []); })
      .catch(() => {});
  }, [fetchQuickNotes]);

  const fetchCompletedTasks = useCallback(() => {
    if (!isLinked) { setCompletedTasks([]); return; }
    api.get(`${API}/zoho/my-completed-tasks-today`)
      .then((res) => setCompletedTasks(res.data?.tasks || []))
      .catch(() => {});
  }, [isLinked]);

  const fetchCompletedCalls = useCallback(() => {
    if (!isLinked) { setCompletedCalls([]); return; }
    api.get(`${API}/zoho/my-completed-calls-today`)
      .then((res) => setCompletedCalls(res.data?.calls || []))
      .catch(() => {});
  }, [isLinked]);

  // Calls: live push when CRM calls change (same event stream the Home page uses), plus a slow safety-net refresh.
  useEffect(() => {
    if (sessionLoading || !isLinked) return undefined;
    let cancelled = false;
    let es = null;
    let retryTimer = null;
    const connect = async () => {
      if (cancelled) return;
      try {
        const res = await api.get(`${API}/calls/sse-token`);
        if (cancelled) return;
        es = new EventSource(`${API}/calls/sse?token=${encodeURIComponent(res.data.token)}`);
        es.addEventListener('calls_changed', () => {
          fetchCalls();
          window.dispatchEvent(new Event(CALLS_CHANGED_EVENT));
        });
        es.onerror = () => {
          es.close();
          if (!cancelled) retryTimer = setTimeout(connect, 5000);
        };
        retryTimer = setTimeout(() => { es.close(); if (!cancelled) connect(); }, 80000);
      } catch (_) { if (!cancelled) retryTimer = setTimeout(connect, 10000); }
    };
    connect();
    return () => { cancelled = true; clearTimeout(retryTimer); if (es) es.close(); };
  }, [fetchCalls, isLinked, sessionLoading]);

  useEffect(() => {
    if (sessionLoading) return undefined;
    fetchCalls();
    fetchCompletedCalls();
    const id = setInterval(() => { if (document.visibilityState === 'visible') fetchCalls(); }, CALLS_SAFETY_NET_MS);
    const onChange = () => { fetchCalls(); fetchCompletedCalls(); };
    window.addEventListener(CALLS_CHANGED_EVENT, onChange);
    return () => { clearInterval(id); window.removeEventListener(CALLS_CHANGED_EVENT, onChange); };
  }, [fetchCalls, fetchCompletedCalls, sessionLoading]);

  useEffect(() => {
    if (sessionLoading) return undefined;
    fetchTasks();
    fetchCompletedTasks();
    const id = setInterval(() => { if (document.visibilityState === 'visible') fetchTasks(); }, TASKS_SAFETY_NET_MS);
    const onChange = () => { fetchTasks(); fetchCompletedTasks(); };
    window.addEventListener(TASKS_CHANGED_EVENT, onChange);
    return () => { clearInterval(id); window.removeEventListener(TASKS_CHANGED_EVENT, onChange); };
  }, [fetchTasks, fetchCompletedTasks, sessionLoading]);

  useEffect(() => {
    if (sessionLoading) return undefined;
    if (!isAdminGroup) { setPiCounts([]); setPiLoading(false); return undefined; }
    fetchPi();
    const id = setInterval(() => { if (document.visibilityState === 'visible') fetchPi(); }, PI_SAFETY_NET_MS);
    window.addEventListener(PI_COUNTS_CHANGED_EVENT, fetchPi);
    return () => { clearInterval(id); window.removeEventListener(PI_COUNTS_CHANGED_EVENT, fetchPi); };
  }, [fetchPi, isAdminGroup, sessionLoading]);

  useEffect(() => {
    if (sessionLoading) return undefined;
    fetchDashboard();
    const id = setInterval(() => { if (document.visibilityState === 'visible') fetchDashboard(); }, DASHBOARD_SAFETY_NET_MS);
    window.addEventListener(STOCK_CHANGED_EVENT, fetchDashboard);
    window.addEventListener(QUOTES_CHANGED_EVENT, fetchDashboard);
    return () => {
      clearInterval(id);
      window.removeEventListener(STOCK_CHANGED_EVENT, fetchDashboard);
      window.removeEventListener(QUOTES_CHANGED_EVENT, fetchDashboard);
    };
  }, [fetchDashboard, sessionLoading]);

  // 8x8 calls: poll, plus an instant push when the 8x8 poller stages a new call for this person.
  useEffect(() => {
    fetch8x8();
    const id = setInterval(fetch8x8, EIGHT_X_EIGHT_POLL_MS);
    return () => clearInterval(id);
  }, [fetch8x8]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return undefined;
    let es = null;
    let retryTimer = null;
    const connect = () => {
      es = new EventSource(`${API}/calls/sse?token=${encodeURIComponent(token)}`);
      es.addEventListener('8x8_calls_changed', fetch8x8);
      es.onerror = () => { es.close(); retryTimer = setTimeout(connect, 5000); };
    };
    connect();
    return () => { if (es) es.close(); clearTimeout(retryTimer); };
  }, [fetch8x8]);

  // Settings > Notifications decides which kinds are shown, exactly as on the Home page.
  const isTypeEnabled = (type) => {
    if (!notifSettings.enabled || !notifSettings.branch_enabled) return false;
    const entry = (notifSettings.notifications || []).find((n) => n.type === type);
    return entry ? (entry.enabled !== false) : true;
  };
  const ready = !settingsLoading && !sessionLoading;
  const show = {
    calls: ready && isTypeEnabled('calls') && isLinked,
    tasks: ready && isTypeEnabled('tasks') && isLinked,
    pi: ready && isTypeEnabled('pi_counts') && isAdminGroup,
    stock: ready && isTypeEnabled('stock') && !stockLoading,
    orders: ready && isTypeEnabled('orders_quotes'),
    eightXEight: true,
  };

  const overdueTasks = useMemo(() => tasks.filter((t) => t.is_overdue), [tasks]);
  const todayTasks = useMemo(() => tasks.filter((t) => t.is_today && !t.is_overdue), [tasks]);
  const overdueCalls = useMemo(() => calls.filter((c) => c.is_overdue), [calls]);
  const todayCalls = useMemo(() => calls.filter((c) => !c.is_overdue), [calls]);
  // `calls_made` from the backend is every still-pending call; the number shown is today's calls to log, as on the Home page.
  const calls8x8MadeToday = useMemo(() => {
    const today = new Date();
    return calls8x8Made.filter((c) => isSameLocalDay(c.created_at, today));
  }, [calls8x8Made]);

  // The number on the right bar's Tasks tab: things that need attention now.
  const attentionCount =
    (show.tasks ? overdueTasks.length + todayTasks.length : 0) +
    (show.calls ? calls.length : 0) +
    calls8x8MadeToday.length;

  const handleCallCompleted = useCallback((callId) => {
    setCalls((previous) => previous.filter((c) => c.id !== callId));
    setActiveCall(null);
    window.dispatchEvent(new Event(CALLS_CHANGED_EVENT));
  }, []);

  const handleCallDeleted = useCallback((callId) => {
    setCalls((previous) => previous.filter((c) => c.id !== callId));
    setActiveCall(null);
    window.dispatchEvent(new Event(CALLS_CHANGED_EVENT));
  }, []);

  const handleTaskComplete = useCallback((taskId) => {
    const completed = tasks.find((t) => t.id === taskId);
    if (completed) setCompletedTasks((previous) => [...previous.filter((t) => t.id !== taskId), completed]);
    setTasks((previous) => previous.filter((t) => t.id !== taskId));
    setSelectedTask(null);
    window.dispatchEvent(new Event(TASKS_CHANGED_EVENT));
  }, [tasks]);

  const handleTaskReactivated = useCallback((taskId) => {
    setCompletedTasks((previous) => previous.filter((t) => t.id !== taskId));
    setSelectedTask(null);
    window.dispatchEvent(new Event(TASKS_CHANGED_EVENT));
  }, []);

  const closeTask = useCallback(() => {
    setSelectedTask(null);
    // Saving a task from its window closes it without telling anyone, so look again to pick up a new follow-up date.
    fetchTasks();
  }, [fetchTasks]);

  const value = useMemo(() => ({
    user,
    show,
    calls, callsLoading, callsRefreshing, manualRefreshCalls, overdueCalls, todayCalls, completedCalls,
    tasks, tasksLoading, overdueTasks, todayTasks, completedTasks,
    piCounts, piLoading,
    stockSummary, stockLoading,
    quotesData,
    calls8x8MadeToday, calls8x8Logged,
    attentionCount,
    openTask: (task, isCompleted = false) => setSelectedTask({ task, isCompleted }),
    openCall: setActiveCall,
    quickNotes, saveQuickNote,
    open8x8: (tab) => { setEightTab(typeof tab === 'string' ? tab : 'made'); setShow8x8(true); },
    openOrders: () => setShowOrders(true),
  }), [
    user, show.calls, show.tasks, show.pi, show.stock, show.orders,
    calls, callsLoading, callsRefreshing, manualRefreshCalls, overdueCalls, todayCalls, completedCalls,
    tasks, tasksLoading, overdueTasks, todayTasks, completedTasks,
    piCounts, piLoading, stockSummary, stockLoading, quotesData, calls8x8MadeToday, calls8x8Logged, attentionCount, quickNotes,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <V3AlertsContext.Provider value={value}>
      {children}
      <AddCallToCrmModal
        isOpen={!!activeCall}
        onClose={() => setActiveCall(null)}
        scheduledCall={activeCall}
        onCompleted={handleCallCompleted}
        onDeleted={handleCallDeleted}
      />
      {selectedTask && (
        <TaskDetailsModal
          task={selectedTask.task}
          isCompleted={selectedTask.isCompleted}
          onClose={closeTask}
          onComplete={handleTaskComplete}
          onReactivated={handleTaskReactivated}
        />
      )}
      <EightXEightCallsModal open={show8x8} onClose={() => setShow8x8(false)} initialTab={eightTab} />
      {showOrders && <OrdersQuotesModal onClose={() => setShowOrders(false)} quotesBreakdown={quotesData} />}
    </V3AlertsContext.Provider>
  );
};

export default V3AlertsContext;
